import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Presentation, Calendar, Users, Clock, Star, Sparkles, Gift, Lightbulb } from 'lucide-react';
import workshopsData from '../content/workshops.json';

const iconMap = {
  Star: Star,
  Gift: Gift,
  Lightbulb: Lightbulb,
  Sparkles: Sparkles,
  Presentation: Presentation,
  Calendar: Calendar,
};

const WorkshopsSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { sectionTitle, description, workshopTypes: workshopTypesData, exampleTopics: exampleTopicsData, flexibilityTitle, flexibilityText } = workshopsData;

  const workshopTypes = workshopTypesData.map(type => ({
    ...type,
    icon: React.createElement(iconMap[type.icon] || Star, { size: 32 })
  }));

  const exampleTopics = exampleTopicsData.map(topic => ({
    ...topic,
    icon: React.createElement(iconMap[topic.icon] || Lightbulb, { size: 28 })
  }));

  return (
    <section id="workshops" className="section-padding bg-white">
      <div className="container-width">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-gradient-to-r from-nitzan-mint/20 to-nitzan-teal/20 px-6 py-3 rounded-full mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <Presentation className="text-nitzan-teal" size={20} />
            <span className="text-nitzan-teal font-medium">סדנאות</span>
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">{sectionTitle}</span>
          </h2>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>
        </motion.div>

        {/* Workshop Types */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {workshopTypes.map((type, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index === 0 ? -30 : 30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.2, duration: 0.6 }}
              className="bg-gradient-to-br from-white to-nitzan-light-bg rounded-3xl shadow-xl overflow-hidden border border-nitzan-sage/20 hover:shadow-2xl transition-all duration-300"
            >
              <div className={`bg-gradient-to-r ${type.gradient} p-8 text-white`}>
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
                  {type.icon}
                </div>
                <h3 className="text-3xl font-bold mb-3">{type.title}</h3>
                <p className="text-white/90 text-lg">{type.description}</p>
              </div>

              <div className="p-8">
                <ul className="space-y-3">
                  {type.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-nitzan-mint to-nitzan-teal flex-shrink-0 mt-2"></div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/contact">
                  <motion.div
                    className={`block text-center bg-gradient-to-r ${type.gradient} text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 mt-8`}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {index === 0 ? 'קבלי עדכונים על סדנאות' : 'הזמיני סדנה'}
                  </motion.div>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Example Topics */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mb-16"
        >
          <h3 className="text-3xl font-bold text-nitzan-teal mb-8 text-center">דוגמאות לנושאי סדנאות</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {exampleTopics.map((topic, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.8 + index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-nitzan-sage/20 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-nitzan-mint to-nitzan-teal rounded-xl flex items-center justify-center text-white mb-4">
                  {topic.icon}
                </div>
                <h4 className="text-xl font-bold text-nitzan-teal mb-2">{topic.title}</h4>
                <p className="text-gray-700 mb-3">{topic.description}</p>
                <div className="flex items-center gap-2 text-nitzan-terracotta text-sm font-semibold">
                  <Clock size={16} />
                  <span>{topic.duration}</span>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-gray-600 mt-8 text-lg">
            <strong className="text-nitzan-teal">ועוד נושאים רבים!</strong> כל סדנה היא חוויה ייחודית.
          </p>
        </motion.div>

        {/* Flexibility Message */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="bg-gradient-to-r from-nitzan-sage/20 via-nitzan-mint/20 to-nitzan-teal/20 rounded-3xl p-8 md:p-12 text-center"
        >
          <Sparkles className="text-nitzan-terracotta mx-auto mb-6" size={48} />
          <h3 className="text-3xl font-bold text-nitzan-teal mb-4">{flexibilityTitle}</h3>
          <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
            {flexibilityText}
          </p>
        </motion.div>

        {/* Upcoming Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.0, duration: 0.8 }}
          className="mt-16 bg-white rounded-3xl p-8 md:p-12 shadow-xl border-2 border-nitzan-mint"
        >
          <div className="text-center">
            <Calendar className="text-nitzan-mint mx-auto mb-6" size={48} />
            <h3 className="text-3xl font-bold text-nitzan-teal mb-4">בקרוב</h3>
            <p className="text-xl text-gray-700 mb-6">
              סדנאות חדשות ומרגשות בתכנון! 
              <br />
              רוצה להיות הראשונה לדעת?
            </p>
            <Link to="/contact">
              <motion.div
                className="inline-block bg-gradient-to-r from-nitzan-teal to-nitzan-mint text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                הירשמי לעדכונים
              </motion.div>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WorkshopsSection;

